// Shared square math keeps the canvas and legend using identical allocation.
export const squareBorderRadiusForSize = (size: number): number => {
  if (size > 10) return 1
  if (size > 4) return 1
  return 0
}

// Compute how many cells fit in a single row given the row layout width.
// `widthScale` stretches each cell's WIDTH to sqSize * widthScale (1 = square,
// 2 = twice as wide/long) while its height stays sqSize.
export const squaresPerRow = (rowWidth: number, sqSize: number, gap: number, widthScale = 1): number => {
  if (sqSize <= 0) return 0
  const slot = sqSize * widthScale + gap
  if (slot <= 0) return 1
  const n = Math.floor((rowWidth + gap) / slot)
  return Math.max(0, n)
}

// Height required to render `count` cells inside one wrapping row group. Row
// height stays sqSize; only the per-row count changes with `widthScale`.
export const requiredHeight = (count: number, rowWidth: number, sqSize: number, gap: number, widthScale = 1): number => {
  if (count <= 0) return 0
  const perRow = squaresPerRow(rowWidth, sqSize, gap, widthScale)
  if (perRow <= 0) return Infinity
  const rows = Math.ceil(count / perRow)
  return rows * sqSize + (rows - 1) * gap
}

// Stateless sizing: recomputed from scratch on every call, so squares shrink
// only when the current total genuinely doesn't fit at startSquareSize, and
// grow back as soon as it does again (e.g. scrolling back up to earlier
// years). Strategy: keep dollarsPerSquare at its initial value and use the
// largest square size in [minSquareSize, startSquareSize] that fits the
// height budget; only when even minSquareSize overflows does dollarsPerSquare
// grow to cap the square count to the rows that fit.
export const resolveSizing = (
  totalValue: number,
  rowWidth: number,
  maxHeight: number,
  startSquareSize: number,
  minSquareSize: number,
  gap: number,
  dollarsPerSquareInitial: number,
  widthScale = 1,
): { squareSize: number; dollarsPerSquare: number } => {
  const dps = dollarsPerSquareInitial
  const count = totalValue > 0 && dps > 0 ? Math.ceil(totalValue / dps) : 0
  for (let s = startSquareSize; s >= minSquareSize; s--) {
    if (requiredHeight(count, rowWidth, s, gap, widthScale) <= maxHeight) {
      return { squareSize: s, dollarsPerSquare: dps }
    }
  }
  const perRowAtMin = squaresPerRow(rowWidth, minSquareSize, gap, widthScale)
  if (perRowAtMin <= 0 || totalValue <= 0) {
    return { squareSize: minSquareSize, dollarsPerSquare: dps }
  }
  const maxRows = Math.max(1, Math.floor((maxHeight + gap) / (minSquareSize + gap)))
  const maxCount = maxRows * perRowAtMin
  const idealDps = Math.ceil(totalValue / maxCount)
  return { squareSize: minSquareSize, dollarsPerSquare: Math.max(dps, idealDps) }
}

export const totalValueForSegments = (segments: { value: number }[]): number => {
  let totalValue = 0
  for (const segment of segments) {
    totalValue += Math.max(0, segment.value)
  }
  return totalValue
}

export const targetSquareCountForSegments = (segments: { value: number }[], dollarsPerSquare: number): number => {
  const totalValue = totalValueForSegments(segments)
  return totalValue > 0 && dollarsPerSquare > 0 ? Math.ceil(totalValue / dollarsPerSquare) : 0
}

export const squareCountsForSegments = (
  segments: { value: number; key?: string }[],
  totalSquareCount: number,
  // Guarantees the listed segment keys at least this many squares, taking them
  // from the largest other segment so the total stays constant. Keeps a
  // tiny-but-important group (e.g. the first AI agents at the scenario start)
  // visible instead of rounding away to zero squares.
  minSquareCountByKey?: Partial<Record<string, number>>,
): number[] => {
  const totalValue = totalValueForSegments(segments)
  const squareCounts: number[] = []
  if (totalSquareCount <= 0 || totalValue <= 0) return squareCounts
  let assigned = 0
  let lastNonZero = -1
  for (let i = 0; i < segments.length; i++) {
    const segment = segments[i]
    const share = Math.round((segment.value / totalValue) * totalSquareCount)
    if (share > 0) lastNonZero = squareCounts.length
    squareCounts.push(share)
    assigned += share
  }
  const delta = totalSquareCount - assigned
  if (delta !== 0 && lastNonZero >= 0) {
    squareCounts[lastNonZero] = Math.max(0, squareCounts[lastNonZero] + delta)
  }
  if (minSquareCountByKey) {
    for (let i = 0; i < segments.length; i++) {
      const key = segments[i].key
      const min = key !== undefined ? minSquareCountByKey[key] ?? 0 : 0
      while (squareCounts[i] < min) {
        let donor = -1
        for (let j = 0; j < squareCounts.length; j++) {
          if (j === i) continue
          if (donor === -1 || squareCounts[j] > squareCounts[donor]) donor = j
        }
        if (donor === -1 || squareCounts[donor] <= 0) break
        squareCounts[donor]--
        squareCounts[i]++
      }
    }
  }
  return squareCounts
}
